import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import invoiceAPI from '../../api/invoiceAPI';
import { formatDate, formatCurrency } from '../../utils/dateUtils';

const InvoiceListPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [pagination, setPagination] = useState({});
    const [success, setSuccess] = useState('');
    
    // Filters and search
    const [filters, setFilters] = useState({
        page: 1,
        limit: 50,
        status: '',
        startDate: '',
        endDate: '',
        customer: '',
        search: ''
    });

    // Summary statistics
    const [summary, setSummary] = useState({
        totalInvoices: 0,
        totalAmount: 0,
        totalDue: 0
    });

    useEffect(() => {
        fetchInvoices();
        fetchSummary();
    }, [filters]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const params = { ...filters };
            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            const response = await invoiceAPI.getAll(params);
            setInvoices(response.data || []);
            setPagination(response.pagination || {});
        } catch (err) {
            setError(err.message || 'Failed to fetch invoices');
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const response = await invoiceAPI.getInvoiceSummary(filters.startDate, filters.endDate);
            setSummary(response.data.totals || {});
        } catch (err) {
            console.error('Failed to fetch summary:', err);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'page' && { page: 1 }) // Reset to page 1 when filters change
        }));
    };

    const handleStatusFilter = (status) => {
        handleFilterChange('status', status === filters.status ? '' : status);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchInvoices();
    };

    const handleClearFilters = () => {
        setFilters({
            page: 1,
            limit: 50,
            status: '',
            startDate: '',
            endDate: '',
            customer: '',
            search: ''
        });
    };

    const handlePageChange = (newPage) => {
        handleFilterChange('page', newPage);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
            sent: { color: 'bg-blue-100 text-blue-800', label: 'Sent' },
            paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
            overdue: { color: 'bg-red-100 text-red-800', label: 'Overdue' },
            void: { color: 'bg-yellow-100 text-yellow-800', label: 'Void' },
            partially_paid: { color: 'bg-orange-100 text-orange-800', label: 'Partial' }
        };

        const config = statusConfig[status] || statusConfig.draft;
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const getDaysOverdue = (dueDate) => {
        const today = new Date();
        const due = new Date(dueDate);
        const diffTime = today - due;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const isOverdue = (invoice) => {
        return invoice.status === 'sent' && new Date(invoice.dueDate) < new Date();
    };

    const handleSendInvoice = async (invoiceId) => {
        if (!window.confirm('Are you sure you want to send this invoice? This will post it to accounting.')) {
            return;
        }

        try {
            await invoiceAPI.send(invoiceId);
            setSuccess('Invoice sent successfully');
            fetchInvoices(); // Refresh the list
        } catch (err) {
            setError(err.message || 'Failed to send invoice');
        }
    };

    const handleMarkAsPaid = async (invoiceId) => {
        if (!window.confirm('Mark this invoice as paid?')) {
            return;
        }

        try {
            await invoiceAPI.markAsPaid(invoiceId, {
                paymentDate: new Date().toISOString().split('T')[0],
                paymentMethod: 'manual',
                notes: 'Marked as paid from invoice list'
            });
            setSuccess('Invoice marked as paid successfully');
            fetchInvoices(); // Refresh the list
        } catch (err) {
            setError(err.message || 'Failed to mark invoice as paid');
        }
    };

    const handleVoidInvoice = async (invoiceId) => {
        if (!window.confirm('Are you sure you want to void this invoice? This action cannot be undone.')) {
            return;
        }

        try {
            await invoiceAPI.void(invoiceId);
            setSuccess('Invoice voided successfully');
            fetchInvoices(); // Refresh the list
        } catch (err) {
            setError(err.message || 'Failed to void invoice');
        }
    };

    const handleExportPDF = async (invoiceId) => {
        try {
            const response = await invoiceAPI.exportToPDF(invoiceId);
            // Create blob and download
            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice-${invoiceId}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || 'Failed to export PDF');
        }
    };

    const statusCounts = invoices.reduce((acc, invoice) => {
        acc[invoice.status] = (acc[invoice.status] || 0) + 1;
        return acc;
    }, {});

    return (
        <Layout pageTitle="Invoices">
            <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <p className="text-gray-600">Manage and track your customer invoices</p>
                    </div>
                    <Link
                        to="/invoice/new"
                        className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 flex items-center"
                    >
                        <span className="mr-2">+</span> Create Invoice
                    </Link>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Invoices</p>
                                <p className="text-2xl font-bold text-gray-900">{summary.totalInvoices}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalAmount)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Amount Due</p>
                                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalDue)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Overdue</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {invoices.filter(inv => isOverdue(inv)).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Quick Filters */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Quick Filters</h3>
                    </div>
                    <div className="p-4">
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => handleStatusFilter('')}
                                className={`px-3 py-2 rounded-md text-sm font-medium ${
                                    filters.status === '' 
                                        ? 'bg-teal-100 text-teal-800 border border-teal-200' 
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                All ({invoices.length})
                            </button>
                            <button
                                onClick={() => handleStatusFilter('draft')}
                                className={`px-3 py-2 rounded-md text-sm font-medium ${
                                    filters.status === 'draft' 
                                        ? 'bg-gray-100 text-gray-800 border border-gray-200' 
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                Draft ({statusCounts.draft || 0})
                            </button>
                            <button
                                onClick={() => handleStatusFilter('sent')}
                                className={`px-3 py-2 rounded-md text-sm font-medium ${
                                    filters.status === 'sent' 
                                        ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                Sent ({statusCounts.sent || 0})
                            </button>
                            <button
                                onClick={() => handleStatusFilter('paid')}
                                className={`px-3 py-2 rounded-md text-sm font-medium ${
                                    filters.status === 'paid' 
                                        ? 'bg-green-100 text-green-800 border border-green-200' 
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                Paid ({statusCounts.paid || 0})
                            </button>
                            <button
                                onClick={() => handleStatusFilter('overdue')}
                                className={`px-3 py-2 rounded-md text-sm font-medium ${
                                    filters.status === 'overdue' 
                                        ? 'bg-red-100 text-red-800 border border-red-200' 
                                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                Overdue ({invoices.filter(inv => isOverdue(inv)).length})
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Filters & Search</h3>
                    </div>
                    <div className="p-4">
                        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Search
                                </label>
                                <input
                                    type="text"
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    placeholder="Search by invoice number, customer..."
                                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                            </div>

                            <div className="flex items-end space-x-2">
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                >
                                    Apply
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClearFilters}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                >
                                    Clear
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Error and Success Messages */}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <strong className="font-bold">Error: </strong>
                                <span>{error}</span>
                            </div>
                            <button
                                onClick={() => setError('')}
                                className="ml-4 text-red-900 hover:text-red-600 text-lg"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}

                {/* Invoices Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="text-lg">Loading invoices...</div>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Invoice
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Customer
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Due Date
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Due
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {invoices.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                                                    No invoices found. <Link to="/invoice/new" className="text-teal-600 hover:text-teal-700">Create your first invoice</Link>
                                                </td>
                                            </tr>
                                        ) : (
                                            invoices.map((invoice) => (
                                                <tr key={invoice._id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {invoice.invoiceNumber}
                                                            </div>
                                                            <div className="text-sm text-gray-500 truncate max-w-xs">
                                                                {invoice.description || 'No description'}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">{invoice.customerName}</div>
                                                        <div className="text-sm text-gray-500">{invoice.customerEmail}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {formatDate(invoice.invoiceDate)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            {formatDate(invoice.dueDate)}
                                                        </div>
                                                        {isOverdue(invoice) && (
                                                            <div className="text-xs text-red-600">
                                                                {getDaysOverdue(invoice.dueDate)} days overdue
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                                        {formatCurrency(invoice.totalAmount)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                                        {formatCurrency(invoice.amountDue)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {getStatusBadge(invoice.status)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <div className="flex justify-end space-x-2">
                                                            <button
                                                                onClick={() => navigate(`/invoice/${invoice._id}`)}
                                                                className="text-teal-600 hover:text-teal-900"
                                                                title="View Details"
                                                            >
                                                                View
                                                            </button>
                                                            
                                                            {invoice.status === 'draft' && (
                                                                <>
                                                                    <button
                                                                        onClick={() => navigate(`/invoice/${invoice.id}/edit`)}
                                                                        className="text-blue-600 hover:text-blue-900"
                                                                        title="Edit"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleSendInvoice(invoice.id)}
                                                                        className="text-green-600 hover:text-green-900"
                                                                        title="Send Invoice"
                                                                    >
                                                                        Send
                                                                    </button>
                                                                </>
                                                            )}
                                                            
                                                            {invoice.status === 'sent' && (
                                                                <button
                                                                    onClick={() => handleMarkAsPaid(invoice._id)}
                                                                    className="text-green-600 hover:text-green-900"
                                                                    title="Mark as Paid"
                                                                >
                                                                    Paid
                                                                </button>
                                                            )}
                                                            
                                                            {(invoice.status === 'draft' || invoice.status === 'sent') && (
                                                                <button
                                                                    onClick={() => handleVoidInvoice(invoice._id)}
                                                                    className="text-red-600 hover:text-red-900"
                                                                    title="Void Invoice"
                                                                >
                                                                    Void
                                                                </button>
                                                            )}
                                                            
                                                            <button
                                                                onClick={() => handleExportPDF(invoice._id)}
                                                                className="text-purple-600 hover:text-purple-900"
                                                                title="Export PDF"
                                                            >
                                                                PDF
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.pages > 1 && (
                                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 flex justify-between items-center">
                                            <div>
                                                <p className="text-sm text-gray-700">
                                                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                                                    <span className="font-medium">
                                                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                                                    </span> of{' '}
                                                    <span className="font-medium">{pagination.total}</span> results
                                                </p>
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handlePageChange(pagination.page - 1)}
                                                    disabled={pagination.page <= 1}
                                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    onClick={() => handlePageChange(pagination.page + 1)}
                                                    disabled={pagination.page >= pagination.pages}
                                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default InvoiceListPage;